import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';

import { ARROW_EDGE_COLORS_BY_VARIANT } from '@/components/diagrams/FlowChart/edges/ArrowEdge';
import { ColorBox } from '@/components/display/ColorBox/ColorBox';
import { COLORS } from '@/styles/tokens/colors';

// Height of the dashed rule swatch's box. `body-xs` is 12px, so an 8px box
// leaves the rule optically centered against the label next to it.
const DASHED_SWATCH_HEIGHT_PX = 8;
const DASHED_SWATCH_WIDTH_PX = 15;

interface EntityDiagramLegendProps {
  presentationMode?: boolean;
  viewOnlyMode?: boolean;
  /**
   * Adds a "Conditional transfer" entry with a dashed-line swatch. Set only
   * when the diagram actually contains conditional (dashed) elements so the
   * legend doesn't explain a style the reader can't see.
   */
  showConditionalTransfers?: boolean;
}

export function EntityDiagramLegend({
  presentationMode,
  viewOnlyMode,
  showConditionalTransfers,
}: EntityDiagramLegendProps) {
  const theme = useTheme();

  const legendItems = useMemo(() => {
    return [
      {
        label: 'In estate',
        color: theme.palette.dataVisualizationPrimary.main,
      },
      {
        label: 'Out of estate (family)',
        color: theme.palette.dataVisualizationSecondary.main,
      },
      {
        label: 'Out of estate (charity)',
        color: theme.palette.dataVisualizationTertiary.main,
      },
    ];
  }, [
    theme.palette.dataVisualizationPrimary.main,
    theme.palette.dataVisualizationSecondary.main,
    theme.palette.dataVisualizationTertiary.main,
  ]);

  // Remove grey background when in presentation mode or view-only mode
  const shouldRemoveBackground = presentationMode || viewOnlyMode;

  return (
    <Stack
      direction="row"
      spacing={2}
      p={1.5}
      sx={{
        backgroundColor: shouldRemoveBackground ? undefined : COLORS.GRAY[100],
        borderTopRightRadius: '4px',
      }}
    >
      {legendItems.map((item) => (
        <Stack
          direction="row"
          alignItems="center"
          key={item.label}
          spacing={0.5}
        >
          <ColorBox color={item.color} />
          <Typography variant="body-xs">{item.label}</Typography>
        </Stack>
      ))}
      {showConditionalTransfers && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {/* ColorBox draws a filled square; a conditional transfer is a line
              style, so the swatch draws the edge's own stroke instead — same
              color, width, and dash rhythm as a dashed ArrowEdge.

              The height must stay an explicit length. MUI's sizing transform
              reads a bare `height={1}` as a ratio and emits `height: 100%`, so
              the swatch grew to the legend row and painted its rule at the top
              of that box — a stray pair of dashes floating well above the
              label, which shipped on every slide with a dashed transfer. */}
          <Box
            component="svg"
            aria-hidden
            data-testid="legend-conditional-transfer-swatch"
            sx={{
              alignSelf: 'center',
              display: 'block',
              flexShrink: 0,
              width: `${DASHED_SWATCH_WIDTH_PX}px`,
              height: `${DASHED_SWATCH_HEIGHT_PX}px`,
            }}
          >
            <line
              x1={0}
              y1={DASHED_SWATCH_HEIGHT_PX / 2}
              x2={DASHED_SWATCH_WIDTH_PX}
              y2={DASHED_SWATCH_HEIGHT_PX / 2}
              stroke={ARROW_EDGE_COLORS_BY_VARIANT.primary}
              strokeWidth={1}
              strokeDasharray="5 5"
            />
          </Box>
          <Typography variant="body-xs">Conditional transfer</Typography>
        </Stack>
      )}
    </Stack>
  );
}
